'use client'

import React, { SyntheticEvent } from 'react'
import Select from 'react-select'
import { SelectComponents } from 'react-select/dist/declarations/src/components'
import { StateManagerProps } from 'react-select/dist/declarations/src/useStateManager'
import useInputWrapper from '../../../hooks/useInputWrapper'
import {
    ClearIndicatorReplacement,
    DropdownIndicatorReplacement,
    MultiLineOption,
    MultiValueComponent,
} from './Components'
import { OptionReplacement } from './Components/OptionReplacement'
import { useId } from 'react'
import { SelectOption, GroupedSelectOption, MultiSelectInputProps } from '../../../types/SelectInput'
import {
    getSelectInputClassNames,
    getSelectContainerClassNames,
    getCustomSelectInputStyles,
    getFilterOption,
} from './Helpers/select-helpers'
import { Text } from '../../atoms'

export const MultiSelectInput = ({
    options = [],
    disabled = false,
    readOnly = false,
    isSearchable = false,
    showAsTag = false,
    showMultiLine = false,
    id,
    onChange,
    onBlur,
    value,
    defaultValue,
    isInline = false,
    className = '',
    placeholder,
    autoFocus = false,
    isClearable = false,
    isLoading = false,
    noOptionsMessage = 'No results',
    menuPosition = 'fixed',
    /** @todo This prop needs to be provided in all use-cases if applicable for accessibility */
    hasError = false,
    ...props
}: MultiSelectInputProps) => {
    const { onFocusHandler, onBlurHandler, classes } = useInputWrapper(false, disabled)
    const randomId = useId()
    const selectId = id ?? randomId

    const components: Partial<SelectComponents<SelectOption, boolean, GroupedSelectOption>> = {
        MultiValue: MultiValueComponent,
        DropdownIndicator: DropdownIndicatorReplacement,
        ClearIndicator: ClearIndicatorReplacement,
        ...(!showMultiLine && !showAsTag && { Option: OptionReplacement }),
    }

    const selectProps: StateManagerProps<SelectOption, boolean, GroupedSelectOption> = {
        instanceId: selectId,
        id: selectId,
        inputId: selectId,
        className: getSelectInputClassNames({ isInline, isMulti: true, isSearchable, showAsTag, readOnly }),
        classNamePrefix: 'select-input',
        isDisabled: disabled,
        isMulti: true,
        closeMenuOnSelect: false,
        hideSelectedOptions: false,
        isSearchable: !readOnly ? isSearchable : false,
        onBlur: (event: SyntheticEvent) => {
            if (onBlur) onBlur(event)
            onBlurHandler()
        },
        menuPlacement: 'auto',
        noOptionsMessage: () => <Text>{noOptionsMessage}</Text>,
        components,
        filterOption: (option, input) => getFilterOption(option, input, showMultiLine),
        styles: getCustomSelectInputStyles(showMultiLine, isSearchable),
        ...(showMultiLine && { formatOptionLabel: MultiLineOption }),
        ...(readOnly && { menuIsOpen: false }),
        ...(placeholder ? { placeholder } : { placeholder: '' }),
        autoFocus,
    }

    return (
        <div
            className={getSelectContainerClassNames(isInline, classes, className)}
            data-cy="input-select"
            data-testid={props['data-testid'] ?? 'select-input'}
        >
            <Select
                {...selectProps}
                isOptionDisabled={(option: SelectOption) => !!option.disabled}
                defaultValue={defaultValue}
                isClearable={!readOnly && isClearable}
                options={options}
                value={options.filter((option) =>
                    Array.isArray(value) ? value?.includes(option.value) : value === option.value,
                )}
                onFocus={onFocusHandler}
                onChange={(option) => onChange && onChange(option as SelectOption & SelectOption[])}
                openMenuOnClick={!readOnly}
                isLoading={isLoading}
                menuPosition={menuPosition}
                aria-invalid={hasError}
            />
        </div>
    )
}
